'use client'

import { useCallback, useMemo, useState } from 'react'

import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import * as ConversationTypes from '@marketplace-web/messaging/conversation-messaging-data'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { BrazeInboxMessage, useBrazeInboxMessageCards } from '@marketplace-web/braze/braze-feature'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import ConversationMessage from './ConversationMessage'
import MessageTime from '../MessageTime'
import { isMessageGrouped, getConversationIdFromUrl, isMessage } from '../../utils'
import PhotosDialog from './PhotosDialog'

type Props = {
  toggleSidebar?: () => void
  openOfferModal?: () => void
}

const ConversationMessages = ({ toggleSidebar, openOfferModal }: Props) => {
  const { relativeUrl } = useBrowserNavigation()
  const conversationId = getConversationIdFromUrl(relativeUrl)
  const { conversation, conversationMessages } = useConversationContext()
  const { user } = useSession()

  const imageOpeningInConversationExperiment = useAbTest('dd_tx_image_opening_in_conversation')
  const isImageOpeningInConversationExperimentOn =
    imageOpeningInConversationExperiment?.variant === 'on'

  const [photosDialogSlide, setPhotosDialogSlide] = useState<number | null>(null)

  const nonHiddenPhotos = useMemo(
    () =>
      conversationMessages.flatMap(conversationMessage => {
        if (!isMessage(conversationMessage)) return []

        return conversationMessage.message.photos.filter(photo => !photo.isHidden)
      }),
    [conversationMessages],
  )

  const hasNonHiddenPhotos = nonHiddenPhotos.length > 0

  useTrackAbTest(imageOpeningInConversationExperiment, hasNonHiddenPhotos)

  const idToIndexMap = useMemo(
    () => Object.fromEntries(nonHiddenPhotos.map((photo, index) => [photo.id, index])),
    [nonHiddenPhotos],
  )

  const closePhotosDialog = useCallback(() => setPhotosDialogSlide(null), [])
  const openPhotoDialog = useCallback(
    (photoId: number) => {
      const index = idToIndexMap[photoId]
      if (index === undefined) return
      setPhotosDialogSlide(index)
    },
    [idToIndexMap],
  )
  const handleSlideInteract = useCallback((slide: number) => setPhotosDialogSlide(slide), [])

  const currentUserId = user?.id
  const oppositeUser = conversation?.oppositeUser

  const { inboxMessageCards } = useBrazeInboxMessageCards()
  const brazeConversationCard = useMemo(
    () => inboxMessageCards?.find(card => card.id === conversationId),
    [conversationId, inboxMessageCards],
  )

  const isCurrentAuthor = (message: ConversationTypes.ConversationMessage) => {
    if (!isMessage(message)) return false

    const { message: entity } = message

    const isPeekingUser =
      entity.userId !== currentUserId?.toString() && oppositeUser?.id !== String(entity?.userId)

    if (isPeekingUser) {
      return String(entity?.userId) !== oppositeUser?.id
    }

    return entity?.userId === currentUserId?.toString()
  }

  if (brazeConversationCard) {
    return (
      <BrazeInboxMessage
        prefix={<MessageTime time={brazeConversationCard.updated.toISOString()} />}
        card={brazeConversationCard}
      />
    )
  }

  if (!conversationMessages.length) {
    return null
  }

  return (
    <>
      {conversationMessages.map((message, index) => (
        <ConversationMessage
          key={index}
          {...message}
          transactionId={conversation?.transactionId}
          isGrouped={isMessageGrouped(message, conversationMessages?.[index - 1])}
          isLast={index + 1 === conversationMessages.length}
          isCurrentAuthor={isCurrentAuthor(message)}
          toggleSidebar={toggleSidebar}
          openOfferModal={openOfferModal}
          conversationId={conversation?.id}
          onThumbnailClick={isImageOpeningInConversationExperimentOn ? openPhotoDialog : undefined}
        />
      ))}
      {isImageOpeningInConversationExperimentOn && hasNonHiddenPhotos && (
        <PhotosDialog
          onClose={closePhotosDialog}
          onSlideInteract={handleSlideInteract}
          slide={photosDialogSlide}
          photos={nonHiddenPhotos}
        />
      )}
    </>
  )
}

export default ConversationMessages
